import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  Building,
  Meter,
  MqttDevice,
  MqttMessageLogEntry,
  MqttMessageStatus,
  MqttStatus,
} from '../types';
import mqttService from '../services/mqttService';
import buildingService from '../services/buildingService';

/** Farbgebung des Verarbeitungsstatus im Rohnachrichten-Log. */
const STATUS_STYLES: Record<MqttMessageStatus, { label: string; className: string }> = {
  OK:             { label: 'Verarbeitet',      className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' },
  BASELINE:       { label: 'Baseline',         className: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 border-sky-200 dark:border-sky-800' },
  TELEMETRY:      { label: 'Telemetrie',       className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700' },
  UNMAPPED:       { label: 'Nicht zugeordnet', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  COUNTER_RESET:  { label: 'Zählerreset',      className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  UNKNOWN_SCHEMA: { label: 'Schema unbekannt', className: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800' },
  IMPLAUSIBLE:    { label: 'Unplausibel',      className: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800' },
  INVALID:        { label: 'Ungültig',         className: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200 dark:border-rose-800' },
  ERROR:          { label: 'Fehler',           className: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200 dark:border-rose-800' },
};

/** Ein Abschnitt der Geräteliste: alle Geräte eines Standorts. */
interface DeviceGroup {
  key: string;
  title: string;
  /** Das Topic, unter dem der Standort meldet, oder warum es keines gibt. */
  hint: string;
  /** Ob sich die Standort-ID zu einem Gebäude auflösen ließ. */
  resolved: boolean;
  devices: MqttDevice[];
}

/**
 * Geräte nach Standort gruppieren.
 *
 * Die Firmware sendet auf `sites/<siteId>/…` und meint mit `siteId` die id des
 * Gebäudes im Portal; das Backend löst sie auf, wo sie sich auflösen lässt.
 * Was übrig bleibt, verschwindet nicht: eine unbekannte Standort-ID bekommt
 * ihre eigene Gruppe, Geräte ganz ohne Standort die Sammelgruppe am Ende. Beide
 * sind genau der Zustand, den ein Admin hier sehen und beheben soll — unter
 * einem geratenen Gebäudenamen wäre er unsichtbar.
 *
 * Sortiert wird nach Gebäudename; die unaufgelösten Standorte und zuletzt die
 * standortlosen Geräte hängen hinten an.
 */
const groupDevicesByBuilding = (devices: MqttDevice[]): DeviceGroup[] => {
  const groups = new Map<string, DeviceGroup>();

  devices.forEach((device) => {
    const site = device.siteId?.trim() ?? '';
    const key = site === '' ? '' : `site:${site}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key: key === '' ? 'ohne-standort' : key,
        title: device.siteBuildingName ?? (site === '' ? 'Ohne Standort' : `Standort ${site}`),
        hint: device.siteBuildingName
          ? `sites/${site}/…`
          : site === ''
            ? 'meldet keine site_id — sendet noch im alten Topic-Baum'
            : 'diese Standort-ID gehört zu keinem Gebäude im Portal',
        resolved: device.siteBuildingName != null,
        devices: [],
      });
    }
    groups.get(key)!.devices.push(device);
  });

  const rank = (g: DeviceGroup): number => (g.resolved ? 0 : g.key === 'ohne-standort' ? 2 : 1);

  return Array.from(groups.values()).sort(
    (a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title, 'de')
  );
};

const MqttSettings: React.FC = () => {
  const [status, setStatus] = useState<MqttStatus | null>(null);
  const [devices, setDevices] = useState<MqttDevice[]>([]);
  const [messages, setMessages] = useState<MqttMessageLogEntry[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [metersMap, setMetersMap] = useState<Record<number, Meter[]>>({});
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [expandedPayload, setExpandedPayload] = useState<number | null>(null);
  const [messageFilter, setMessageFilter] = useState<string>('');
  const [messageDeviceFilter, setMessageDeviceFilter] = useState<string>('');
  // Standardmäßig nur die aktiven Geräte: die Liste wächst mit jedem Gerät,
  // das sich je gemeldet hat, und wer hier hinschaut, meint fast immer den
  // aktuellen Betrieb. Ist keines online, bietet die leere Liste den Schalter
  // zum Aufheben an.
  const [onlineOnly, setOnlineOnly] = useState(true);

  const loadStatusAndDevices = useCallback(async () => {
    const [statusResult, deviceList] = await Promise.all([
      mqttService.getStatus(),
      mqttService.getDevices(),
    ]);
    setStatus(statusResult);
    setDevices(deviceList);
  }, []);

  const loadMessages = useCallback(async (statusFilter: string, deviceFilter: string) => {
    const list = await mqttService.getMessages({
      limit: 100,
      status: statusFilter === '' ? undefined : statusFilter,
      deviceId: deviceFilter === '' ? undefined : deviceFilter,
    });
    setMessages(list);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      await loadStatusAndDevices();
      await loadMessages(messageFilter, messageDeviceFilter);

      const buildingList = await buildingService.getAll();
      setBuildings(buildingList);
      const meters: Record<number, Meter[]> = {};
      await Promise.all(
        buildingList.map(async (b) => {
          try {
            meters[b.id] = await buildingService.getMeters(b.id);
          } catch {
            meters[b.id] = [];
          }
        })
      );
      setMetersMap(meters);
    } catch {
      toast.error('MQTT-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [loadStatusAndDevices, loadMessages, messageFilter, messageDeviceFilter]);

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Status und Nachrichten regelmäßig nachladen — MQTT-Daten treffen laufend ein
  useEffect(() => {
    const timer = setInterval(() => {
      loadStatusAndDevices().catch(() => undefined);
      loadMessages(messageFilter, messageDeviceFilter).catch(() => undefined);
    }, 15000);
    return () => clearInterval(timer);
  }, [loadStatusAndDevices, loadMessages, messageFilter, messageDeviceFilter]);

  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      const result = await mqttService.reconnect();
      if (result.connected) {
        toast.success('Verbindung hergestellt');
      } else {
        toast.error('Verbindung fehlgeschlagen');
      }
      await loadStatusAndDevices();
    } catch {
      toast.error('Reconnect fehlgeschlagen');
    } finally {
      setReconnecting(false);
    }
  };

  const handleMapChannel = async (channelId: number, value: string) => {
    try {
      await mqttService.mapChannel(channelId, value === '' ? null : Number(value));
      toast.success(
        value === ''
          ? 'Zuordnung aufgehoben'
          : 'Zugeordnet — der nächste Zählerstand setzt die Baseline'
      );
      await loadStatusAndDevices();
    } catch {
      toast.error('Zuordnung fehlgeschlagen');
    }
  };

  /** Einspeise-Zählwerk eines Netzzähler-Kanals auf einen PV-Zähler buchen. */
  const handleFeedInMeter = async (channelId: number, value: string) => {
    try {
      await mqttService.setFeedInMeter(channelId, value === '' ? null : Number(value));
      toast.success(
        value === ''
          ? 'Einspeise-Ziel aufgehoben'
          : 'Einspeisung wird ab der nächsten Nachricht auf den PV-Zähler gebucht'
      );
      await loadStatusAndDevices();
    } catch {
      toast.error('Einspeise-Ziel konnte nicht gesetzt werden');
    }
  };

  const handleRename = async (device: MqttDevice) => {
    const name = window.prompt(`Anzeigename für ${device.deviceId}`, device.name ?? '');
    if (name === null) return;
    try {
      await mqttService.renameDevice(device.id, name.trim() === '' ? null : name.trim());
      toast.success('Name gespeichert');
      await loadStatusAndDevices();
    } catch {
      toast.error('Name konnte nicht gespeichert werden');
    }
  };

  const handleDelete = async (device: MqttDevice) => {
    if (!window.confirm(`Gerät ${device.deviceId} aus der Liste entfernen?`)) return;
    try {
      await mqttService.deleteDevice(device.id);
      toast.success('Gerät entfernt');
      await loadStatusAndDevices();
    } catch {
      toast.error('Gerät konnte nicht entfernt werden');
    }
  };

  const handleSendInterval = async (device: MqttDevice) => {
    const input = window.prompt(
      `Sendeintervall in Sekunden für ${device.deviceId}`,
      String(device.intervalSeconds ?? 300)
    );
    if (input === null) return;
    const intervalSeconds = Number(input);
    if (!Number.isFinite(intervalSeconds) || intervalSeconds < 10) {
      toast.error('Intervall muss mindestens 10 Sekunden sein');
      return;
    }
    try {
      await mqttService.publishConfig(device.id, { intervalSeconds });
      toast.success('Konfiguration gesendet');
    } catch {
      toast.error('Konfiguration konnte nicht gesendet werden');
    }
  };

  const applyMessageFilters = async (statusFilter: string, deviceFilter: string) => {
    setMessageFilter(statusFilter);
    setMessageDeviceFilter(deviceFilter);
    setExpandedPayload(null);
    try {
      await loadMessages(statusFilter, deviceFilter);
    } catch {
      toast.error('Nachrichten konnten nicht geladen werden');
    }
  };

  /** Aus der Geräteliste heraus direkt zum gefilterten Nachrichten-Log springen. */
  const handleShowDeviceMessages = async (device: MqttDevice) => {
    await applyMessageFilters(messageFilter, device.deviceId);
    document.getElementById('mqtt-messages')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const allMeters = buildings.flatMap((b) =>
    (metersMap[b.id] || []).map((m) => ({ ...m, buildingName: b.name }))
  );
  const pvMeters = allMeters.filter((m) => m.type === 'PV');

  const onlineDeviceCount = devices.filter((d) => d.online).length;
  const visibleDevices = onlineOnly ? devices.filter((d) => d.online) : devices;
  const deviceGroups = groupDevicesByBuilding(visibleDevices);

  // Ein Filter kann auf ein Gerät zeigen, das nicht mehr in der Liste steht (z. B. entfernt)
  const deviceFilterOptions: Array<{ deviceId: string; name: string | null }> =
    messageDeviceFilter !== '' && !devices.some((d) => d.deviceId === messageDeviceFilter)
      ? [...devices, { deviceId: messageDeviceFilter, name: null }]
      : devices;

  const formatTimeSince = (dateStr?: string | null): string => {
    if (!dateStr) return '—';
    const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (Number.isNaN(diffMin)) return '—';
    if (diffMin < 1) return 'gerade eben';
    if (diffMin < 60) return `vor ${diffMin} min`;
    if (diffMin < 1440) return `vor ${Math.floor(diffMin / 60)} h`;
    return `vor ${Math.floor(diffMin / 1440)} d`;
  };

  const formatUptime = (seconds?: number | null): string => {
    if (seconds == null) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d} d ${h} h`;
    if (h > 0) return `${h} h ${m} min`;
    return `${m} min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#216dab]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Kopfzeile */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight transition-colors">
            MQTT Integration
          </h1>
          <p className="text-slate-400 dark:text-slate-500 text-base font-normal transition-colors">
            Messwerte, die Gebäude-Zähler direkt an den Broker senden
          </p>
        </div>
        <button
          onClick={handleReconnect}
          disabled={reconnecting || !status?.enabled}
          className="px-6 py-3 bg-[#216dab] hover:bg-[#1a5a8f] disabled:opacity-40 text-white rounded-2xl font-bold text-sm shadow-lg shadow-[#216dab]/25 transition-all flex items-center gap-2"
        >
          {reconnecting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Neu verbinden
        </button>
      </div>

      {/* Hinweis, wenn der Ingest gar nicht aktiv ist */}
      {status && !status.enabled && (
        <div className="glass-card p-6 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <p className="text-sm font-bold text-amber-800 dark:text-amber-400">MQTT-Ingest ist deaktiviert</p>
          <p className="text-sm text-amber-700 dark:text-amber-500 mt-1">
            Setze <code className="font-mono">MQTT_ENABLED=true</code> sowie{' '}
            <code className="font-mono">MQTT_BROKER_URL</code> in der Backend-Konfiguration.
            Eingehende Nachrichten werden bis dahin nicht verarbeitet.
          </p>
        </div>
      )}

      {/* Statuskarten */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-3 h-3 rounded-full ${status?.connected ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
            <p className="text-sm font-medium text-slate-500">Broker</p>
          </div>
          <p className="text-lg font-bold text-slate-800 dark:text-slate-200">
            {status?.connected ? 'Verbunden' : 'Getrennt'}
          </p>
          <p className="text-sm text-slate-400 font-mono mt-1 break-all">{status?.brokerUrl || '—'}</p>
          {status?.lastError && (
            <p className="text-sm text-rose-500 mt-2 break-words">{status.lastError}</p>
          )}
        </div>

        <div className="glass-card p-6">
          <p className="text-sm font-medium text-slate-500 mb-3">Geräte</p>
          <p className="text-lg font-bold text-slate-800 dark:text-slate-200">
            {status?.onlineDeviceCount ?? 0} / {status?.deviceCount ?? 0} online
          </p>
          {(status?.unmappedChannelCount ?? 0) > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
              {status?.unmappedChannelCount} Kanal/Kanäle ohne Zuordnung
            </p>
          )}
        </div>

        <div className="glass-card p-6">
          <p className="text-sm font-medium text-slate-500 mb-3">Nachrichten (seit Start)</p>
          <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{status?.messagesReceived ?? 0}</p>
          <p className="text-sm text-slate-400 mt-1">{status?.readingsStored ?? 0} Messwerte gespeichert</p>
        </div>

        <div className="glass-card p-6">
          <p className="text-sm font-medium text-slate-500 mb-3">Nicht verarbeitet</p>
          <p className={`text-lg font-bold ${(status?.messagesFailed ?? 0) > 0 ? 'text-rose-500' : 'text-slate-800 dark:text-slate-200'}`}>
            {status?.messagesFailed ?? 0}
          </p>
          <p className="text-sm text-slate-400 mt-1 font-mono break-all">
            {status?.subscribedTopics?.join(', ') || '—'}
          </p>
        </div>
      </div>

      {/* Geräte und Kanalzuordnung */}
      <div className="glass-card overflow-hidden">
        <div className="p-6 border-b border-black/5 dark:border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-slate-500">Geräte &amp; Kanalzuordnung</h2>
            <p className="text-sm text-slate-400 mt-1">
              Ein Kanal liefert erst Daten in die Auswertung, wenn er einem Zähler zugeordnet ist
            </p>
          </div>
          <button
            onClick={() => setOnlineOnly((v) => !v)}
            aria-pressed={onlineOnly}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 border ${
              onlineOnly
                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                : 'bg-black/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 border-transparent hover:bg-black/10 dark:hover:bg-white/10'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${onlineOnly ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            Nur aktive Geräte ({onlineDeviceCount}/{devices.length})
          </button>
        </div>

        {devices.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-base font-bold text-slate-400 dark:text-slate-600">Noch keine Geräte gesehen</p>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">
              Geräte registrieren sich selbst, sobald sie die erste Nachricht auf{' '}
              <code className="font-mono">meters/&lt;deviceId&gt;/data</code> senden.
            </p>
          </div>
        ) : visibleDevices.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-base font-bold text-slate-400 dark:text-slate-600">Kein Gerät ist aktuell online</p>
            <button
              onClick={() => setOnlineOnly(false)}
              className="text-sm font-medium text-[#216dab] hover:underline mt-2"
            >
              Alle {devices.length} Geräte anzeigen
            </button>
          </div>
        ) : (
          <div className="divide-y divide-black/5 dark:divide-white/5">
            {deviceGroups.map((group) => (
              <div key={group.key}>
                {/* Standortkopf. Die Gruppe steht auch dann da, wenn der
                    Standort sich nicht auflösen ließ — mit dem Grund. */}
                <div className="px-6 py-3 bg-black/[0.03] dark:bg-white/[0.03] flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{group.title}</span>
                  <span className={`text-sm ${group.resolved ? 'text-slate-400 font-mono' : 'text-amber-600 dark:text-amber-500'}`}>
                    {group.hint}
                  </span>
                  <span className="ml-auto text-sm text-slate-400 whitespace-nowrap">
                    {group.devices.length} {group.devices.length === 1 ? 'Gerät' : 'Geräte'}
                  </span>
                </div>

                <div className="divide-y divide-black/5 dark:divide-white/5">
                  {group.devices.map((device) => (
                    <div key={device.id} className="p-6 space-y-4">
                      {/* Gerätekopf */}
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-2.5 h-2.5 rounded-full ${device.online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                          <div>
                            <p className="text-base font-bold text-slate-800 dark:text-slate-200">
                              {device.name || device.deviceId}
                            </p>
                            <p className="text-sm text-slate-500 font-mono">{device.deviceId}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button onClick={() => handleShowDeviceMessages(device)} className="px-3 py-2 text-sm font-medium rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 transition-all">
                            Nachrichten
                          </button>
                          <button onClick={() => handleRename(device)} className="px-3 py-2 text-sm font-medium rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 transition-all">
                            Umbenennen
                          </button>
                          <button onClick={() => handleSendInterval(device)} disabled={!status?.connected} className="px-3 py-2 text-sm font-medium rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-40 text-slate-600 dark:text-slate-300 transition-all">
                            Intervall senden
                          </button>
                          <button onClick={() => handleDelete(device)} className="px-3 py-2 text-sm font-medium rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-500 transition-all">
                            Entfernen
                          </button>
                        </div>
                      </div>

                      {/* Telemetrie */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                        {[
                          ['Letzte Nachricht', formatTimeSince(device.lastMessageAt)],
                          ['Firmware', device.fwVersion ?? '—'],
                          ['Bautyp', device.fwTarget ?? '—'],
                          ['Build-Hash', device.elfSha256 ?? '—'],
                          ['IP', device.ipAddress ?? '—'],
                          ['WLAN', device.wifiRssi != null ? `${device.wifiRssi} dBm` : '—'],
                          ['Laufzeit', formatUptime(device.uptimeSeconds)],
                          ['Intervall', device.intervalSeconds != null ? `${device.intervalSeconds} s` : '—'],
                        ].map(([label, value]) => (
                          <div key={label as string} className="p-3 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl border border-black/5 dark:border-white/5">
                            <p className="text-slate-400">{label}</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300 truncate">{value}</p>
                          </div>
                        ))}
                      </div>

                      {device.errorCount > 0 && (
                        <p className="text-sm text-rose-500">
                          {device.errorCount} fehlerhafte Nachricht(en) von {device.messageCount} insgesamt
                        </p>
                      )}

                      {/* Kanäle */}
                      {device.channels.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">
                          Noch keine Messkanäle gemeldet — das Gerät hat bisher nur Telemetrie gesendet.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-sm">
                            <thead className="bg-black/[0.02] dark:bg-white/[0.02]">
                              <tr>
                                <th className="px-4 py-3 font-medium text-slate-400">Kanal</th>
                                <th className="px-4 py-3 font-medium text-slate-400">Zähler</th>
                                <th className="px-4 py-3 font-medium text-slate-400">Letzter Stand</th>
                                <th className="px-4 py-3 font-medium text-slate-400">Messwerte</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5 dark:divide-white/5">
                              {device.channels.map((channel) => (
                                <tr key={channel.id}>
                                  <td className="px-4 py-3 font-mono font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                    {channel.channelKey}
                                  </td>
                                  <td className="px-4 py-3">
                                    <select
                                      value={channel.meterId ?? ''}
                                      onChange={(e) => handleMapChannel(channel.id, e.target.value)}
                                      className="bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 text-sm font-medium text-slate-800 dark:text-slate-300 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 cursor-pointer w-full min-w-[16rem] transition-colors"
                                    >
                                      <option value="">— Nicht zugeordnet —</option>
                                      {allMeters.map((m) => (
                                        <option key={m.id} value={m.id}>
                                          {m.buildingName} → {m.name || m.type} ({m.type})
                                        </option>
                                      ))}
                                    </select>
                                    {/* Ein Netzzähler führt zwei Zählwerke: Bezug und
                                        Einspeisung. Der Bezug geht oben auf den
                                        Stromzähler; die Einspeisung gehört zur PV-Anlage
                                        und bekommt hier ihr eigenes Ziel. Nur bei
                                        Strom-Kanälen — ein PV-Zähler bucht seine
                                        eigene Einspeisung ohnehin. */}
                                    {channel.meterType === 'STROM' && pvMeters.length > 0 && (
                                      <div className="mt-1.5 flex items-center gap-2">
                                        <span className="text-sm text-slate-400 whitespace-nowrap">Einspeisung →</span>
                                        <select
                                          value={channel.feedInMeterId ?? ''}
                                          onChange={(e) => handleFeedInMeter(channel.id, e.target.value)}
                                          className="bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 text-sm text-slate-800 dark:text-slate-300 px-3 py-1.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 cursor-pointer w-full min-w-[12rem] transition-colors"
                                        >
                                          <option value="">— nicht gebucht —</option>
                                          {pvMeters.map((m) => (
                                            <option key={m.id} value={m.id}>
                                              {m.buildingName} → {m.name || m.type}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                    {/* Zwei Quellen für denselben Standort: was das
                                        Gerät meldet und wohin der Kanal zeigt. Weichen
                                        sie ab, ist eines von beidem falsch — das
                                        gehört sichtbar, nicht weggruppiert. */}
                                    {channel.buildingName &&
                                      device.siteBuildingName &&
                                      channel.buildingName !== device.siteBuildingName && (
                                        <p className="text-sm text-amber-600 dark:text-amber-500 mt-1.5">
                                          Gerät meldet „{device.siteBuildingName}", der Zähler
                                          gehört zu „{channel.buildingName}"
                                        </p>
                                      )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                    {channel.lastTotal != null
                                      ? `${channel.lastTotal.toLocaleString('de-DE', { maximumFractionDigits: 3 })} ${channel.lastUnit ?? ''}`
                                      : channel.lastValue != null
                                        ? `${channel.lastValue.toLocaleString('de-DE', { maximumFractionDigits: 3 })} ${channel.lastUnit ?? ''} (roh)`
                                        : '—'}
                                    <span className="block text-slate-400">{formatTimeSince(channel.lastTotalAt)}</span>
                                  </td>
                                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{channel.readingCount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rohnachrichten */}
      <div id="mqtt-messages" className="glass-card overflow-hidden scroll-mt-6">
        <div className="p-6 border-b border-black/5 dark:border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-slate-500">Eingegangene Nachrichten</h2>
            <p className="text-sm text-slate-400 mt-1">
              Auch nicht verarbeitete Nachrichten werden gespeichert — hier ist das echte Payload ablesbar
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={messageDeviceFilter}
              onChange={(e) => applyMessageFilters(messageFilter, e.target.value)}
              className="bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 text-sm font-medium text-slate-800 dark:text-slate-300 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 cursor-pointer transition-colors"
            >
              <option value="">Alle Geräte</option>
              {deviceFilterOptions.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.name ? `${d.name} (${d.deviceId})` : d.deviceId}
                </option>
              ))}
            </select>
            <select
              value={messageFilter}
              onChange={(e) => applyMessageFilters(e.target.value, messageDeviceFilter)}
              className="bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 text-sm font-medium text-slate-800 dark:text-slate-300 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 cursor-pointer transition-colors"
            >
              <option value="">Alle Status</option>
              {Object.keys(STATUS_STYLES).map((key) => (
                <option key={key} value={key}>
                  {STATUS_STYLES[key as MqttMessageStatus].label}
                </option>
              ))}
            </select>
            {(messageFilter !== '' || messageDeviceFilter !== '') && (
              <button
                onClick={() => applyMessageFilters('', '')}
                className="px-3 py-2 text-sm font-medium rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 transition-all"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">Keine Nachrichten im gewählten Filter</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-black/5 dark:border-white/5">
                <tr>
                  <th className="px-4 py-3 font-medium text-slate-400 whitespace-nowrap">Zeit</th>
                  <th className="px-4 py-3 font-medium text-slate-400">Topic</th>
                  <th className="px-4 py-3 font-medium text-slate-400">Status</th>
                  <th className="px-4 py-3 font-medium text-slate-400">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/5">
                {messages.map((message) => {
                  const style = STATUS_STYLES[message.status] ?? STATUS_STYLES.TELEMETRY;
                  const isOpen = expandedPayload === message.id;
                  return (
                    <tr
                      key={message.id}
                      onClick={() => setExpandedPayload(isOpen ? null : message.id)}
                      className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02] cursor-pointer transition-colors align-top"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">
                        {new Date(message.receivedAt).toLocaleString('de-DE')}
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-600 dark:text-slate-400 break-all">
                        {message.topic}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-lg border text-sm font-medium whitespace-nowrap ${style.className}`}>
                          {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        <p className="break-words">{message.detail || '—'}</p>
                        {isOpen && message.payload && (
                          <pre className="mt-2 p-3 bg-slate-900 text-slate-100 rounded-xl overflow-x-auto text-sm font-mono whitespace-pre-wrap break-all">
                            {message.payload}
                          </pre>
                        )}
                        {!isOpen && message.payload && (
                          <p className="text-slate-400 mt-1">Payload anzeigen …</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MqttSettings;
